import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWindows, faLinux, faApple } from '@fortawesome/free-brands-svg-icons';

import { styled, alpha } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';

import SearchIcon from '@mui/icons-material/Search';

import { useAuth } from 'context/auth';
import { useAgents } from 'hooks/organizations';

const SearchBox = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 1),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(3)})`,
    //transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

function Search({ onSearch }) {

  const navigate = useNavigate();
  const { selectedOrgId } = useAuth();
  const { data: agents } = useAgents(selectedOrgId);

  const [inputValue, setInputValue] = useState('');

  const connectToAgent = (agent) => {
    navigate(`/agents/${agent.id}`);
    onSearch && onSearch();
  };

  const getIcon = (os) => {
    switch (os) {
      case 'windows':
        return <FontAwesomeIcon icon={faWindows} />;
      case 'linux':
        return <FontAwesomeIcon icon={faLinux} />;
      case 'darwin':
        return <FontAwesomeIcon icon={faApple} />;
      default:
        return null;
    }
  };

  return (
    <SearchBox>
      <SearchIconWrapper>
        <SearchIcon />
      </SearchIconWrapper>
      <Autocomplete
        id="search-agents"
        options={agents ? agents.map(a => { return { label: a.hostname, os: a.os ? a.os : '' }; }) : []}
        noOptionsText="No agents"
        autoHighlight
        value={null}
        inputValue={inputValue}
        onInputChange={(e, val) => {
          setInputValue(val);
        }}
        onChange={(e, val) => {
          const agent = agents.filter(a => a.hostname === val.label);
          if (agent.length === 1) {
            setInputValue('');
            connectToAgent(agent[0]);
          }
        }}
        renderInput={(params) => (
          <StyledInputBase
            inputRef={params.InputProps.ref}
            placeholder="Search agents..."
            inputProps={{ 'aria-label': 'search', ...params.inputProps }}
          />
        )}
        renderOption={(props, option) => (
          <Box sx={{ fontSize: 14 }} component="li" {...props}>
            {option.os && (<Box sx={{ mr: 2}}>{getIcon(option.os)}</Box>)}
            {option.label}
          </Box>
        )}
      />
    </SearchBox>
  );
}

export default Search;